// Promos venta del Portal Admin VentasLuga, con el diseño del mock: KPIs, pestañas y lista con
// detalle editable. Crear, editar y cambiar el estado pasan en el panel de la derecha.
import { useCallback, useMemo, useState } from 'react';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import { Button, ConfirmDialog, usePermissions } from '@/kit';
import { mxDay } from '@/lib/dateHelpers';
import { PageTabs } from '@/shared/components/PageTabs';
import { LifecycleConfirmDialog, LifecycleMasterDetail } from '@/shared/patterns/Lifecycle';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { screenFrameClass } from '@/shared/patterns/ScreenFrame';
import type {
    SalePromotion,
    SalePromotionListItem,
    SalePromotionTransition,
} from './api/promotionsContract';
import { usePromotionFormSources } from './api/usePromotionFormSources';
import { useOwnRetailerName } from './lib/useOwnRetailerName';
import { usePromotionsRetailers } from './lib/usePromotionsRetailers';
import { useMultiRetailerCreate } from './lib/useMultiRetailerCreate';
import { ChoiceChips } from '@/shared/patterns/EditorForm';
import { ALL_RETAILERS } from './constants/retailerFilter';
import { MULTI_RETAILER_COPY } from './constants/helpText';
import { mergeReferralLists } from './lib/referralKpis';
import {
    useChangeSalePromotionStatus,
    useCombinedRetailerLists,
    usePromotionsConfigured,
    useReferralPromotionsByRetailer,
    useSalePromotions,
    useSalePromotionsByRetailer,
} from './api/usePromotions';
import { useSaveSalePromotion, useUpdateSystemPromotion } from './api/useSalePromotionWrites';
import { DiscountCodesPanel } from './components/DiscountCodesPanel';
import { PromotionLogPanel } from './components/PromotionLogPanel';
import { PromotionsKpiBar } from './components/PromotionsKpiBar';
import { ReferralPromotionKpis } from './components/ReferralPromotionKpis';
import { PromotionsListPanel } from './components/PromotionsListPanel';
import { PromotionsServiceState } from './components/PromotionsServiceState';
import { ReferralPromotionsPanel } from './components/ReferralPromotionsPanel';
import { SalePromotionEditor } from './components/SalePromotionEditor';
import { WelcomeBonusEditor } from './components/WelcomeBonusEditor';
import {
    ACTION_COPY,
    saveSuccessCopy,
    statusChangeErrorDescription,
} from './lib/promotionActionsCopy';
import { statusConfirmation } from './components/promotionLifecycle';
import { resolvePromotionsAvailability } from './lib/promotionsFormat';
import {
    DEFAULT_LIST_SORT,
    EMPTY_LIST_FILTERS,
    filterPromotions,
    sortPromotions,
    type PromotionListFilters,
    type PromotionListSort,
} from './lib/promotionsList';
import {
    formValuesFromPromotion,
    toCreateSalePromotionRequest,
    type SalePromotionFormValues,
} from './lib/salePromotionForm';
import { newPromotionFormValues } from './lib/salePromotionDraft';
import {
    SAVE_ERROR_TITLE,
    saveErrorMessage,
    welcomeBonusSaveErrorMessage,
} from './lib/salePromotionSaveError';
import {
    isSystemPromotion,
    toWelcomeBonusUpdateRequest,
    type WelcomeBonusFormValues,
} from './lib/systemPromotion';
import { useSalePromotionPrefill } from './lib/useSalePromotionPrefill';
import { PROMOTIONS_PERMISSIONS } from './permissions';

type PageTab = 'promos' | 'referrals' | 'codes' | 'audit';

/** Qué muestra el panel derecho. `skus` llega de «Crear promo venta» en Inventario. */
type Selection =
    | { kind: 'none' }
    | { kind: 'existing'; promotionId: string }
    | { kind: 'new'; draftKey: number; skus?: string[] };

interface PendingAction {
    promo: SalePromotionListItem;
    transition: SalePromotionTransition;
}

const NONE: Selection = { kind: 'none' };

/** Vencida y archivada ya no se editan; lo demás guarda una versión nueva. */
function isEditableStatus(promo: SalePromotionListItem): boolean {
    return promo.status !== 'EXPIRED' && promo.status !== 'ARCHIVED';
}

function asListItem(promo: SalePromotion): SalePromotionListItem {
    return { ...promo, redemptionsLast30Days: 0 };
}

export function PromotionsPage() {
    const { hasPermission } = usePermissions();
    const canList = hasPermission(PROMOTIONS_PERMISSIONS.list);
    const canReadLog = hasPermission(PROMOTIONS_PERMISSIONS.readLog);
    const canListCodes = hasPermission(PROMOTIONS_PERMISSIONS.codeList);
    const canCreate = hasPermission(PROMOTIONS_PERMISSIONS.create);
    const canUpdate = hasPermission(PROMOTIONS_PERMISSIONS.update);
    const prefill = useSalePromotionPrefill();

    // El reloj se fija al montar: recalcularlo en cada render movería la vigencia bajo el cursor.
    const [now] = useState(() => new Date());
    const today = useMemo(() => mxDay(now.toISOString()), [now]);

    const [tab, setTab] = useState<PageTab>('promos');
    const [filters, setFilters] = useState<PromotionListFilters>(EMPTY_LIST_FILTERS);
    const [sort, setSort] = useState<PromotionListSort>(DEFAULT_LIST_SORT);
    const [selection, setSelection] = useState<Selection>(() =>
        prefill.prefill && canCreate
            ? { kind: 'new', draftKey: 0, skus: [prefill.prefill.sku] }
            : NONE,
    );
    const [dirty, setDirty] = useState(false);
    const [pendingSelection, setPendingSelection] = useState<Selection | null>(null);
    const [confirming, setConfirming] = useState<PendingAction | null>(null);
    const [saved, setSaved] = useState<SalePromotionListItem | null>(null);
    const [retailerFilter, setRetailerFilter] = useState(ALL_RETAILERS);
    const [auditRetailerId, setAuditRetailerId] = useState('');

    const chains = usePromotionsRetailers();
    const { multi } = chains;
    const draft = useMultiRetailerCreate(chains.nameOf);
    const configured = usePromotionsConfigured();
    const archived = filters.status === 'ARCHIVED';
    // La pestaña de referido lee las suyas aparte: sin `kind` el back no las trae.
    const referrals = useSalePromotions({
        archived: false,
        enabled: canList && !multi,
        kind: 'REFERRAL',
        channel: 'SALE',
    });
    const single = useSalePromotions({ archived, enabled: canList && !multi });
    const byRetailer = useSalePromotionsByRetailer({
        archived,
        retailerIds: chains.retailerIds,
        enabled: canList && multi,
    });
    const combined = useCombinedRetailerLists(byRetailer, chains.loading);
    // Sin la lista de cadenas no hay promos que leer: se muestra como falla con reintento.
    const chainsError = multi ? chains.error : null;
    const list = !multi
        ? single
        : chainsError
          ? { ...combined, isSuccess: false, error: chainsError, refetch: chains.refetch }
          : combined;
    const referralsByRetailer = useReferralPromotionsByRetailer({
        channel: 'SALE',
        retailerIds: chains.retailerIds,
        enabled: canList && multi,
    });
    const referralCount = multi ? mergeReferralLists(referralsByRetailer) : referrals;
    const changeStatus = useChangeSalePromotionStatus();
    const save = useSaveSalePromotion();
    const saveSystem = useUpdateSystemPromotion();
    const availability = resolvePromotionsAvailability({
        configured,
        isSuccess: list.isSuccess,
        error: list.error,
    });

    const selectedId = selection.kind === 'existing' ? selection.promotionId : null;
    // Recién creada puede no venir todavía en la lista: se muestra la que devolvió el guardado.
    const selected =
        selectedId === null
            ? null
            : (list.items.find((promo) => promo.promotionId === selectedId) ??
              (saved?.promotionId === selectedId ? saved : null));

    // Con varias cadenas, el catálogo y las tiendas son los de la cadena de la promo o de la única elegida.
    const activeRetailerId =
        selection.kind === 'new'
            ? draft.selectedIds.length === 1
                ? draft.selectedIds[0]
                : undefined
            : selected?.retailerId;
    const sources = usePromotionFormSources(canList, multi ? activeRetailerId : undefined);
    const ownRetailerName = useOwnRetailerName();
    const retailerColumn = ownRetailerName
        ? ({ mode: 'fixed', label: ownRetailerName } as const)
        : undefined;

    const segmentNames = useMemo(
        () => new Map(sources.segments.map((segment) => [segment.segmentId, segment.name])),
        [sources.segments],
    );
    const productNames = useMemo(
        () =>
            new Map(
                sources.products.map((product) => [
                    product.sku,
                    [product.brand, product.model].filter(Boolean).join(' ') || product.sku,
                ]),
            ),
        [sources.products],
    );
    const visible = useMemo(
        () =>
            sortPromotions(
                filterPromotions(
                    list.items.filter(
                        (promo) =>
                            retailerFilter === ALL_RETAILERS || promo.retailerId === retailerFilter,
                    ),
                    filters,
                    now,
                ),
                sort,
                now,
                segmentNames,
            ),
        [list.items, retailerFilter, filters, now, sort, segmentNames],
    );

    // Como el mock, al entrar se abre la primera promo de la lista; solo la primera vez que carga.
    const [autoOpened, setAutoOpened] = useState(false);
    const [firstVisible] = visible;
    if (!autoOpened && visible.length > 0) {
        setAutoOpened(true);
        if (selection.kind === 'none') {
            setSelection({ kind: 'existing', promotionId: firstVisible.promotionId });
        }
    }

    const applySelection = useCallback(
        (next: Selection) => {
            if (selection.kind === 'new' && selection.skus) prefill.clear();
            save.reset();
            saveSystem.reset();
            setDirty(false);
            setSelection(next);
        },
        [prefill, save, saveSystem, selection],
    );

    const requestSelection = (next: Selection) => {
        if (dirty) setPendingSelection(next);
        else applySelection(next);
    };

    const startNew = () => {
        const ids = chains.retailerIds;
        draft.reset(ids.length === 1 ? [ids[0]] : []);
        requestSelection({ kind: 'new', draftKey: Date.now() });
    };

    /** Con varias cadenas, cada escritura va a la de la promo; sin ellas, a la del token. */
    const writeTarget = (promo: SalePromotionListItem | null) =>
        multi && promo ? { retailerId: promo.retailerId } : {};

    /** Una copia por cadena elegida, sin repetir las ya creadas; abre la primera al terminar. */
    const createInRetailers = async (values: SalePromotionFormValues): Promise<boolean> => {
        const request = toCreateSalePromotionRequest(values);
        const result = await draft.run(
            (retailerId) => save.mutateAsync({ request, retailerId }),
            (error) => ({ message: saveErrorMessage(error) }),
        );
        if (!result.ok) return false;
        setSaved(asListItem(result.first));
        applySelection({ kind: 'existing', promotionId: result.first.promotionId });
        return true;
    };

    const onSave = async (values: SalePromotionFormValues): Promise<boolean> => {
        const promotionId = selection.kind === 'existing' ? selection.promotionId : undefined;
        if (multi && promotionId === undefined) return createInRetailers(values);
        try {
            const promo = await save.mutateAsync({
                promotionId,
                request: toCreateSalePromotionRequest(
                    values,
                    promotionId === undefined ? 'create' : 'update',
                ),
                ...writeTarget(selected),
            });
            const copy = saveSuccessCopy(promo, promotionId === undefined);
            toast.success(copy.title, { description: copy.description });
            setSaved(asListItem(promo));
            if (promotionId === undefined) {
                applySelection({ kind: 'existing', promotionId: promo.promotionId });
            }
            return true;
        } catch (error) {
            toast.error(SAVE_ERROR_TITLE, { description: saveErrorMessage(error) });
            return false;
        }
    };

    const onSaveWelcomeBonus = async (
        promotionId: string,
        values: WelcomeBonusFormValues,
    ): Promise<boolean> => {
        try {
            const promo = await saveSystem.mutateAsync({
                promotionId,
                request: toWelcomeBonusUpdateRequest(values),
                ...writeTarget(selected),
            });
            const copy = saveSuccessCopy(promo, false);
            toast.success(copy.title, { description: copy.description });
            setSaved(asListItem(promo));
            return true;
        } catch (error) {
            toast.error(SAVE_ERROR_TITLE, { description: welcomeBonusSaveErrorMessage(error) });
            return false;
        }
    };

    const send = ({ promo, transition }: PendingAction) => {
        const copy = ACTION_COPY[transition];
        changeStatus.mutate(
            { promotionId: promo.promotionId, transition, ...writeTarget(promo) },
            {
                onSuccess: () => {
                    setConfirming(null);
                    toast.success(copy.done, { description: promo.name });
                },
                onError: (error) => {
                    setConfirming(null);
                    toast.error(`No se pudo ${copy.verb} la promo`, {
                        description: statusChangeErrorDescription(error),
                    });
                },
            },
        );
    };

    const onAction = (promo: SalePromotionListItem, transition: SalePromotionTransition) => {
        if (ACTION_COPY[transition].confirm) setConfirming({ promo, transition });
        else send({ promo, transition });
    };

    // Las del mock primero; «Códigos y cupones» va antes de «Auditoría».
    const tabs = [
        { key: 'promos', label: 'Promos venta' },
        {
            key: 'referrals',
            label: 'Promos de referido',
            ...(referralCount.isSuccess
                ? { count: referralCount.items.length, countTitle: 'Promos de referido cargadas' }
                : {}),
        },
        ...(canListCodes ? [{ key: 'codes', label: 'Códigos y cupones' }] : []),
        ...(canReadLog ? [{ key: 'audit', label: 'Auditoría' }] : []),
    ];

    if (!canList) {
        return (
            <div className="p-2">
                <PromotionsServiceState kind="forbidden" />
            </div>
        );
    }

    if (availability === 'unavailable' || availability === 'forbidden') {
        return (
            <div className="p-2">
                <PromotionsServiceState
                    kind={availability}
                    onRetry={() => {
                        void list.refetch();
                    }}
                />
            </div>
        );
    }

    return (
        <div className={screenFrameClass('fill')}>
            {tab === 'referrals' ? (
                <ReferralPromotionKpis
                    channel="SALE"
                    enabled={canList}
                    now={now}
                    {...(multi ? { retailerIds: chains.retailerIds } : {})}
                />
            ) : tab === 'codes' ? null : (
                <PromotionsKpiBar kpis={list.kpis} />
            )}
            <PageTabs
                tabs={tabs}
                active={tab}
                onChange={(key) => {
                    setTab(key as PageTab);
                }}
            />
            {renderTab()}
            {renderStatusConfirm()}
            <ConfirmDialog
                open={pendingSelection !== null}
                onOpenChange={(open) => {
                    if (!open) setPendingSelection(null);
                }}
                title="Cambios sin guardar"
                description="Vas a cambiar de promo y perderás los cambios en la actual."
                confirmLabel="Descartar cambios"
                destructive
                onConfirm={() => {
                    if (pendingSelection) applySelection(pendingSelection);
                    setPendingSelection(null);
                }}
            />
        </div>
    );

    function renderTab() {
        if (tab === 'audit') {
            return renderAudit();
        }
        if (tab === 'codes') {
            return (
                <DiscountCodesPanel
                    sources={sources}
                    today={today}
                    {...(multi
                        ? {
                              retailers: {
                                  ids: chains.retailerIds,
                                  loading: chains.loading,
                                  names: chains.retailerNames,
                                  options: chains.options,
                                  error: chains.error,
                                  refetch: chains.refetch,
                              },
                          }
                        : {})}
                />
            );
        }
        if (tab === 'referrals') {
            return (
                <ReferralPromotionsPanel
                    channel="SALE"
                    sources={sources}
                    ownRetailerName={ownRetailerName}
                    {...(multi
                        ? { retailers: chains.retailers ?? [], retailersLoading: chains.loading }
                        : {})}
                    now={now}
                    today={today}
                />
            );
        }
        return (
            <LifecycleMasterDetail
                list={
                    <PromotionsListPanel
                        items={visible}
                        loaded={list.isSuccess || availability === 'failed'}
                        now={now}
                        filters={filters}
                        onFiltersChange={setFilters}
                        sort={sort}
                        onSortChange={setSort}
                        segmentNames={segmentNames}
                        productNames={productNames}
                        selectedId={selectedId}
                        onSelect={(promo) => {
                            if (promo.promotionId === selectedId) return;
                            requestSelection({ kind: 'existing', promotionId: promo.promotionId });
                        }}
                        canCreate={canCreate}
                        onCreate={startNew}
                        emptyText={emptyText()}
                        footer={renderListFooter()}
                        {...(multi
                            ? {
                                  retailers: {
                                      names: chains.retailerNames,
                                      value: retailerFilter,
                                      onChange: setRetailerFilter,
                                  },
                              }
                            : {})}
                    />
                }
                detail={renderDetail()}
            />
        );
    }

    /** El historial global; con varias cadenas se lee el de la cadena elegida. */
    function renderAudit() {
        const auditRetailer = auditRetailerId || chains.retailerIds[0];
        return (
            <div className="min-h-0 flex-1 overflow-y-auto p-3">
                {multi && chains.options.length > 1 ? (
                    <div className="mb-3">
                        <ChoiceChips
                            label="Cadena del historial"
                            options={chains.options}
                            value={auditRetailer}
                            onPick={setAuditRetailerId}
                        />
                    </div>
                ) : null}
                {multi && !auditRetailer ? null : (
                    <PromotionLogPanel {...(multi ? { retailerId: auditRetailer } : {})} />
                )}
            </div>
        );
    }

    function emptyText(): string {
        if (availability === 'failed') return '';
        if (list.items.length > 0) return 'Sin promos que coincidan.';
        return archived ? 'No hay promos archivadas.' : 'No hay promos de venta todavía.';
    }

    function renderListFooter() {
        if (availability === 'failed') {
            return (
                <div className="p-2">
                    <PromotionsServiceState
                        kind="failed"
                        detail={list.error instanceof Error ? list.error.message : undefined}
                        onRetry={() => {
                            void list.refetch();
                        }}
                    />
                </div>
            );
        }
        if (!list.hasNextPage) return null;
        return (
            <div className="flex flex-none justify-center border-t border-border p-2">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                        void list.fetchNextPage();
                    }}
                    isLoading={list.isFetchingNextPage}
                >
                    Cargar más
                </Button>
            </div>
        );
    }

    function renderDetail() {
        if (selection.kind === 'new') {
            return (
                <SalePromotionEditor
                    key={`new#${String(selection.draftKey)}`}
                    promotion={null}
                    defaultValues={newPromotionFormValues(today, selection.skus)}
                    sources={sources}
                    retailer={
                        multi
                            ? {
                                  mode: 'choose',
                                  options: chains.options,
                                  selected: draft.selectedIds,
                                  createdIds: draft.createdIds,
                                  loading: chains.loading,
                                  onToggle: draft.toggle,
                                  error: draft.error,
                              }
                            : retailerColumn
                    }
                    locked={draft.locked}
                    notice={multi && selection.skus ? MULTI_RETAILER_COPY.prefill : undefined}
                    productNames={productNames}
                    now={now}
                    today={today}
                    editable={canCreate}
                    canReadLog={canReadLog}
                    saving={save.isPending}
                    onSave={onSave}
                    onCloseNew={() => {
                        applySelection(NONE);
                    }}
                    onDirtyChange={setDirty}
                    onAction={() => undefined}
                />
            );
        }
        if (selected && isSystemPromotion(selected)) {
            return (
                <WelcomeBonusEditor
                    key={`${selected.promotionId}#${String(selected.version)}`}
                    promotion={selected}
                    now={now}
                    editable={canUpdate && isEditableStatus(selected)}
                    canReadLog={canReadLog}
                    saving={saveSystem.isPending}
                    onSave={(values) => onSaveWelcomeBonus(selected.promotionId, values)}
                    onDirtyChange={setDirty}
                    onAction={(transition) => {
                        onAction(selected, transition);
                    }}
                />
            );
        }
        if (selected) {
            return (
                <SalePromotionEditor
                    key={`${selected.promotionId}#${String(selected.version)}`}
                    promotion={selected}
                    defaultValues={formValuesFromPromotion(selected)}
                    sources={sources}
                    retailer={
                        multi
                            ? { mode: 'fixed', label: chains.nameOf(selected.retailerId) }
                            : retailerColumn
                    }
                    productNames={productNames}
                    now={now}
                    today={today}
                    editable={canUpdate && isEditableStatus(selected)}
                    canReadLog={canReadLog}
                    saving={save.isPending}
                    onSave={onSave}
                    onCloseNew={() => undefined}
                    onDirtyChange={setDirty}
                    onAction={(transition) => {
                        onAction(selected, transition);
                    }}
                />
            );
        }
        return (
            <MasterDetailEmpty
                icon={<Star className="size-10" aria-hidden />}
                title="Selecciona una promoción"
                sub="Elige una de la lista para verla o editarla, o crea una nueva con «Crear promoción»."
            />
        );
    }

    function renderStatusConfirm() {
        return (
            <LifecycleConfirmDialog
                confirmation={
                    confirming ? statusConfirmation(confirming.promo, confirming.transition) : null
                }
                loading={changeStatus.isPending}
                onCancel={() => {
                    setConfirming(null);
                }}
                onConfirm={() => {
                    if (confirming) send(confirming);
                }}
            />
        );
    }
}
